"use client";

import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { Info } from "lucide-react";
import { TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
export function AnalyticsInfoTip(t0) {
  const $ = _c(8);
  const {
    label,
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Info className="h-3.5 w-3.5 shrink-0" aria-hidden={true} />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== label) {
    t2 = <TooltipTrigger asChild={true}><button type="button" className="-m-1 inline-flex rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" aria-label={label}>{t1}</button></TooltipTrigger>;
    $[1] = label;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== children) {
    t3 = <TooltipContent side="top" className="max-w-xs text-left leading-snug">{children}</TooltipContent>;
    $[3] = children;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t2 || $[6] !== t3) {
    t4 = <TooltipPrimitive.Root>{t2}{t3}</TooltipPrimitive.Root>;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
}
export interface AnalyticsStatTileProps {
  title: string;
  value: ReactNode;
  icon: ReactNode;
  highlight?: boolean;
  className?: string;
  titleTooltip?: string;
  subtitle?: string;
  detail?: string;
  methodologyTooltip?: string;
  methodologyAriaLabel?: string;
  uniformHeight?: boolean;
}
export function AnalyticsStatTile(t0) {
  const $ = _c(37);
  const {
    title,
    value,
    icon,
    highlight,
    className,
    titleTooltip,
    subtitle,
    detail,
    methodologyTooltip,
    methodologyAriaLabel,
    uniformHeight
  } = t0;
  const t1 = uniformHeight && "min-h-[4.75rem]";
  const t2 = highlight && "border-primary/30 bg-primary/[0.04] ring-1 ring-primary/10 dark:bg-primary/[0.07]";
  let t3;
  if ($[0] !== className || $[1] !== t1 || $[2] !== t2) {
    t3 = cn("flex flex-col gap-2 rounded-xl border bg-card p-3 shadow-sm transition-shadow hover:shadow-sm", t1, t2, className);
    $[0] = className;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== title) {
    t4 = <span className="min-w-0">{title}</span>;
    $[4] = title;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== title || $[7] !== titleTooltip) {
    t5 = titleTooltip ? <AnalyticsInfoTip label={`About ${title}`}>{titleTooltip}</AnalyticsInfoTip> : null;
    $[6] = title;
    $[7] = titleTooltip;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== methodologyAriaLabel || $[10] !== methodologyTooltip || $[11] !== title) {
    t6 = methodologyTooltip ? <AnalyticsInfoTip label={methodologyAriaLabel ?? `How ${title} is calculated`}>{methodologyTooltip}</AnalyticsInfoTip> : null;
    $[9] = methodologyAriaLabel;
    $[10] = methodologyTooltip;
    $[11] = title;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t4 || $[14] !== t5 || $[15] !== t6) {
    t7 = <div className="min-w-0 flex flex-wrap items-center gap-1 text-sm font-medium leading-tight text-muted-foreground">{t4}{t5}{t6}</div>;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const t8 = highlight && "bg-primary/10 text-primary";
  let t9;
  if ($[17] !== t8) {
    t9 = cn("flex h-8 w-8 shrink-0 items-center justify-center self-start rounded-lg bg-muted text-muted-foreground", t8);
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== icon || $[20] !== t9) {
    t10 = <span className={t9} aria-hidden={true}>{icon}</span>;
    $[19] = icon;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== t10 || $[23] !== t7) {
    t11 = <div className="flex items-start justify-between gap-2">{t7}{t10}</div>;
    $[22] = t10;
    $[23] = t7;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== value) {
    t12 = <div className="text-2xl font-semibold tracking-tight tabular-nums">{value}</div>;
    $[25] = value;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== subtitle) {
    t13 = subtitle ? <p className="text-sm leading-snug text-muted-foreground">{subtitle}</p> : null;
    $[27] = subtitle;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== detail) {
    t14 = detail ? <p className="text-xs leading-snug text-muted-foreground">{detail}</p> : null;
    $[29] = detail;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== t11 || $[32] !== t12 || $[33] !== t13 || $[34] !== t14 || $[35] !== t3) {
    t15 = <div className={t3}>{t11}{t12}{t13}{t14}</div>;
    $[31] = t11;
    $[32] = t12;
    $[33] = t13;
    $[34] = t14;
    $[35] = t3;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  return t15;
}